export type AbbreviationMap = ReadonlyMap<string, string>;

export interface AbbreviationExpansionResult {
	value: string;
	cursorPosition: number;
}

interface ExpandAbbreviationAtCursorOptions {
	value: string;
	previousValue: string;
	cursorPosition: number | null | undefined;
	abbreviationMap: AbbreviationMap | null | undefined;
}

const TRAILING_PUNCT = /[.,;:!?]+$/;
const POSSESSIVE = /['’]s?$/;

// A trigger token: word characters plus apostrophes, with optional trailing
// punctuation. Widened from `\w+` so "atty's" and "atty," reach the matcher.
const TRIGGER_AT_CURSOR = /([\w'’]+[.,;:!?]*)\s$/;

// Lowercased trigger→replacement index, memoized per source map identity so the
// case-insensitive fallback doesn't rebuild on every keystroke. Sources memoize
// their maps, so identities are stable across renders. On collision the first
// entry wins (deterministic; exact-case matches are still tried first anyway).
const lowerIndexCache = new WeakMap<AbbreviationMap, Map<string, string>>();
function lowerIndexOf(map: AbbreviationMap): Map<string, string> {
	let index = lowerIndexCache.get(map);
	if (!index) {
		index = new Map();
		for (const [trigger, replacement] of map) {
			const key = trigger.toLowerCase();
			if (!index.has(key)) index.set(key, replacement);
		}
		lowerIndexCache.set(map, index);
	}
	return index;
}

export function expandAbbreviationAtCursor({
	value,
	previousValue,
	cursorPosition,
	abbreviationMap,
}: ExpandAbbreviationAtCursorOptions): AbbreviationExpansionResult | null {
	if (!abbreviationMap?.size) return null;
	if (cursorPosition === null || cursorPosition === undefined) return null;
	if (value.length !== previousValue.length + 1) return null;

	const insertedCharacterIndex = cursorPosition - 1;
	if (insertedCharacterIndex < 0 || value[insertedCharacterIndex] !== " ") {
		return null;
	}

	const beforeCursor = value.slice(0, cursorPosition);
	const afterCursor = value.slice(cursorPosition);
	const match = beforeCursor.match(TRIGGER_AT_CURSOR);
	if (!match) return null;

	const replacement = resolveTrigger(match[1], abbreviationMap);
	if (replacement === undefined) return null;

	const wordStart = beforeCursor.length - match[0].length;
	const newBefore = `${beforeCursor.slice(0, wordStart)}${replacement} `;

	return {
		value: newBefore + afterCursor,
		cursorPosition: newBefore.length,
	};
}

/**
 * Resolve a typed token to its expansion. An exact-case trigger always wins;
 * otherwise the lookup falls back to a case-insensitive match, so a lone "atty"
 * fires for any capitalization while distinct "Atty"/"atty" entries stay
 * separate. Also tolerates a trailing possessive and/or punctuation: it peels
 * the suffix, looks up the base trigger, and re-attaches it — so "atty's,"
 * expands to "attorney's," without a dedicated abbreviation.
 */
function resolveTrigger(token: string, map: AbbreviationMap): string | undefined {
	const lookup = (word: string): string | undefined =>
		map.get(word) ?? lowerIndexOf(map).get(word.toLowerCase());

	const exact = lookup(token);
	if (exact !== undefined) return exact;

	const punct = token.match(TRAILING_PUNCT)?.[0] ?? "";
	const stem = punct ? token.slice(0, -punct.length) : token;
	const poss = stem.match(POSSESSIVE)?.[0] ?? "";
	const base = poss ? stem.slice(0, -poss.length) : stem;
	if (!punct && !poss) return undefined;

	const replacement = lookup(base);
	return replacement !== undefined ? replacement + poss + punct : undefined;
}
